import React, { useEffect, useState } from 'react';
import { dialogConfigs } from '../Utils/PMHelper';
import { TParty, TPartyType } from '../Types/party.types';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import { toast } from 'Components/DesignSystem/Toast/useToast';

import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

type CreatePartyDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    isLoading?: boolean;
    editData?: TParty | null;
    mode?: 'create' | 'edit';
    id?: string;
}

/** The three fields, in `dialogConfigs.createParty` order — unchanged. */
type PartyForm = {
    partyName: string;
    partyType: string;
    address: string;
};

const EMPTY_FORM: PartyForm = { partyName: '', partyType: '', address: '' };

/**
 * Create / Edit Party on the design-system `Dialog` (§ 118).
 *
 * It was `Components/CustomDialog` driven by a `dialogConfigs.createParty` field
 * descriptor — a generic form renderer that switch-cased over `field.type`. That
 * indirection is gone **for the party dialog only**: `CustomDialog` still serves the
 * Domain, Channel and Environment screens untouched, so this migration cannot move
 * them. `dialogConfigs.createParty` is still the source of the title and the field
 * ORDER, which is why it is still imported.
 *
 * Three defects the descriptor form shipped, all fixed by the swap rather than by a
 * patch:
 *
 * - **No testids.** Every hook in `CustomDialog` reads `id ? `${id}-save-btn` :
 *   undefined`, and no caller ever passed `id` — so Save, Cancel and Close on this
 *   dialog had **no `data-testid` at all**.
 * - **Duplicate testids.** Its text input hardcodes `components-customdialog-input-1`,
 *   and Party Name and Address are both `type: 'text'` — two elements, one testid, on
 *   one page.
 * - **Nothing was labelled.** `<label>` with no `htmlFor`, `<input>` with no `id`;
 *   `getByLabelText` could not work and a screen reader announced three unnamed
 *   fields. `TextField`/`Dropdown` own that association.
 *
 * `Dialog` also brings `role="dialog"`, `aria-modal`, a focus trap, focus restore to
 * the trigger, and Escape-to-dismiss, none of which the hand-rolled overlay had.
 * Escape routes to the same guarded `onClose` as the X, so it cannot fire mid-save.
 *
 * **Behaviour held byte-identical** (this migration is presentational): the
 * `partytype/list` fetch on open and both its toast messages; `isFetchingTypes`
 * folded into `isLoading` so the whole form is inert until the types land; the
 * create-mode payload passed through as `{ partyName, partyType, address }` for
 * `ManageParty` to map; the edit-mode payload carrying `active` and `sams_id` from
 * `editData`; the "Creating..." busy label — which is wrong in edit mode, and stays
 * wrong here on purpose, because changing it changes observable text (audit finding
 * 16, deliberately not fixed).
 */
export const CreatePartyDialog = ({ isOpen, onClose, onSave, isLoading = false, editData = null, mode = 'create', id }: CreatePartyDialogProps) => {
    const [partyTypes, setPartyTypes] = useState<TPartyType[]>([]);
    const [isFetchingTypes, setIsFetchingTypes] = useState(false);
    const [formData, setFormData] = useState<PartyForm>(EMPTY_FORM);

    const baseId = id || `party-${mode}-dialog`;
    /* One flag for every disabled/busy decision, exactly as `CustomDialog` derived it
       from `isLoading || isFetchingTypes`. */
    const busy = isLoading || isFetchingTypes;

    useEffect(() => {
        if (isOpen) {
            fetchPartyTypes();
        }
    }, [isOpen]);

    /**
     * `CustomDialog` seeded its form from `field.defaultValue` behind a
     * `hasInitialized` latch, and cleared it on close. Same observable contract, but
     * the clearing happens on OPEN rather than on close: seeding covers both modes on
     * the render that opens, so a reset in the close branch would be unreachable —
     * nothing reads this state while the dialog is unmounted, and every reopen
     * overwrites it. A redundant reset is a line no test can hold in place.
     *
     * Keyed on `isOpen` alone, deliberately: re-seeding on every render would discard
     * what the user had typed the moment `partytype/list` resolved.
     */
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (mode === 'edit' && editData) {
            setFormData({
                partyName: editData.name,
                partyType: editData.party_type || '',
                address: editData.address || '',
            });
        } else {
            setFormData(EMPTY_FORM);
        }
    }, [isOpen]);

    const fetchPartyTypes = async () => {
        setIsFetchingTypes(true);
        try {
            const partyTypeResponse = await ServiceWrapper.doGet( `${api_base_url}partytype/list`, null, null, AuthManager.getGmsToken(),); 
            const response = partyTypeResponse?.data;
            if (response && response.done && response.data) {
                setPartyTypes(response.data);
            } else {
                toast.error('Failed to load party types');
            }
        } catch (error) {
            console.error('Error fetching party types:', error);
            toast.error('Error loading party types');
        } finally {
            setIsFetchingTypes(false);
        }
    };

    const setField = (name: keyof PartyForm, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /**
     * The option list is the API's, not `dialogConfigs`'. The descriptor's hardcoded
     * five were always overwritten by `partytype/list` — including its
     * `{ value: "Seller", label: "Customer" }`, a duplicated label that has never
     * been visible because of that. Building straight from `partyTypes` keeps it that
     * way rather than leaving a trap for the next reader.
     */
    const partyTypeOptions = partyTypes.map(type => ({ value: type.name, label: type.name }));

    const handleSave = () => {
        if (mode === 'edit' && editData) {
            onSave({
                name: formData.partyName,
                address: formData.address,
                party_type: formData.partyType,
                active: editData.active,
                sams_id: editData.sams_id,
            });
        } else {
            onSave(formData);
        }
    };

    /* `CustomDialog` refused to close while loading, on the X and on Cancel alike.
       `Dialog` routes Escape here too, so the guard covers all three. */
    const handleClose = () => {
        if (!busy) {
            onClose();
        }
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            title={mode === 'edit' ? 'Edit Party' : dialogConfigs.createParty.title}
            size="md"
            data-testid={baseId}
            footer={
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <Button
                        variant="secondary"
                        id={`${baseId}-cancel-btn`}
                        data-testid={`${baseId}-cancel-btn`}
                        onClick={handleClose}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        id={`${baseId}-save-btn`}
                        data-testid={`${baseId}-save-btn`}
                        onClick={handleSave}
                        /* § 209 — the spinner spins only for the real save
                           (isLoading); the types fetch keeps the plain disabled
                           gate via `busy`, exactly as before. */
                        disabled={busy}
                        loading={isLoading}
                        className="tw-min-w-[92px]"
                    >
                        {busy ? 'Creating...' : (mode === 'edit' ? 'Update' : 'Create')}
                    </Button>
                </div>
            }
        >
            <div className="tw-space-y-3">
                <TextField
                    label="Party Name"
                    required
                    placeholder="Enter"
                    value={formData.partyName}
                    onChange={(value) => setField('partyName', value)}
                    disabled={busy}
                    id={`${baseId}-name`}
                    data-testid={`${baseId}-name`}
                />
                {/* `usePortal`: the listbox would otherwise be clipped by the dialog's
                    own scrolling content region — the § 104 rule for a `<select>`
                    migrated inside any overflow container. */}
                <Dropdown
                    label="Party Type"
                    required
                    placeholder="Select"
                    usePortal
                    options={partyTypeOptions}
                    value={formData.partyType}
                    onChange={(value) => setField('partyType', String(value))}
                    disabled={busy}
                    id={`${baseId}-type`}
                    data-testid={`${baseId}-type`}
                />
                <TextField
                    label="Address"
                    required
                    placeholder="Enter"
                    value={formData.address}
                    onChange={(value) => setField('address', value)}
                    disabled={busy}
                    id={`${baseId}-address`}
                    data-testid={`${baseId}-address`}
                />
            </div>
        </Dialog>
    );
};
